import React, { useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { CONFIG } from '../config';

const AddGuestPage = ({ allowedChannels = 'both', customGroups = null }) => {
  const smsEnabled = allowedChannels === 'sms' || allowedChannels === 'both';
  const waEnabled = allowedChannels === 'whatsapp' || allowedChannels === 'both';

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [msgPreferences, setMsgPreferences] = useState({
    sms: allowedChannels === 'sms',
    whatsapp: allowedChannels === 'whatsapp',
  });
  const [response, setResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const groupOptions = customGroups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'];

  const handleMsgPrefChange = (type) => {
    setMsgPreferences(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleGroupChange = (group) => {
    setSelectedGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setResponse({ success: false, message: 'Name is required' });
      return;
    }
    if (!phoneNumber.trim()) {
      setResponse({ success: false, message: 'Phone number is required' });
      return;
    }
    if (selectedGroups.length === 0) {
      setResponse({ success: false, message: 'Please select at least one group' });
      return;
    }
    if (!msgPreferences.sms && !msgPreferences.whatsapp) {
      setResponse({ success: false, message: 'Please select at least one message preference (SMS or WhatsApp)' });
      return;
    }

    try {
      setSubmitting(true);
      setResponse(null);

      // Build groups array including message preferences
      const allGroups = [...selectedGroups];
      if (msgPreferences.sms) allGroups.push('SMS');
      if (msgPreferences.whatsapp) allGroups.push('WhatsApp');

      const payload = {
        name: name.trim(),
        phone_number: phoneNumber.trim(),
        groups: allGroups,
      };

      const resp = await fetch(`${CONFIG.apiBaseUrl}/add_guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      // Check if response is JSON
      const contentType = resp.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await resp.json();
      } else {
        // If not JSON, get the text to see what the error is
        const text = await resp.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 200)}`);
      }
      
      if (resp.ok) {
        setResponse({
          success: true,
          message: 'Guest added successfully!',
          data
        });

        // Clear form on success
        setName('');
        setPhoneNumber('');
        setSelectedGroups([]);
        setMsgPreferences({ sms: false, whatsapp: false });
      } else {
        setResponse({
          success: false,
          message: data.error || 'Failed to add guest',
          data
        });
      }
    } catch (error) {
      console.error('Add user error:', error);
      setResponse({ 
        success: false, 
        message: error.message || 'Network error occurred' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setName('');
    setPhoneNumber('');
    setSelectedGroups([]);
    setMsgPreferences({ sms: false, whatsapp: false });
    setResponse(null);
  };

  return (
    <div className="adduser-page">
      <h2 className="page-title">
        Add New Contact
      </h2>

      <form onSubmit={handleSubmit} className="adduser-form">
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            Name: <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter full name"
            className="form-input"
            disabled={submitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber" className="form-label">
            Phone Number: <span className="required">*</span>
          </label>
          <PhoneInput
            id="phoneNumber"
            defaultCountry="US"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value || '')}
            placeholder="(832) 555-0123"
            disabled={submitting}
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            Groups: <span className="required">*</span>
          </label>
          <div className="checkbox-group">
            {groupOptions.map(group => (
              <label key={group} className="checkbox-label">
                <input
                  type="checkbox"
                  value={group}
                  checked={selectedGroups.includes(group)}
                  onChange={() => handleGroupChange(group)}
                  className="checkbox-input"
                  disabled={submitting}
                />
                <span>{group}</span>
              </label>
            ))}
          </div>
        </div>

        {(smsEnabled && waEnabled) && (
          <div className="form-group">
            <label className="form-label">
              Message Preference: <span className="required">*</span>
            </label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={msgPreferences.sms}
                  onChange={() => handleMsgPrefChange('sms')}
                  className="checkbox-input"
                  disabled={submitting}
                />
                <span>SMS</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={msgPreferences.whatsapp}
                  onChange={() => handleMsgPrefChange('whatsapp')}
                  className="checkbox-input"
                  disabled={submitting}
                />
                <span>WhatsApp</span>
              </label>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-submit"
            disabled={submitting}
          >
            {submitting ? 'Adding Guest...' : 'Add Guest'}
          </button>
          
          <button 
            type="button" 
            onClick={handleReset}
            className="btn btn-reset"
            disabled={submitting}
          >
            Reset Form
          </button>
        </div>

        {response && (
          <div className={`response-box ${response.success ? 'success' : 'error'}`}>
            <strong>
              {response.success ? '✓ Success' : '✗ Error'}
            </strong>
            <p>{response.message}</p>
            {response.data && (
              <pre>
                {JSON.stringify(response.data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default AddGuestPage;